// A tooltip only where a screen asks for one, and over a text cut short
// (`cut-text-tooltip.ts`). Nothing else shows tooltips: the shell never uses
// `title`, and SVGs carry no <title>.
//
// Promise: shown after a short rest of the pointer, or on keyboard focus;
// hidden on leave, blur, press, click and Escape. Placed above the element
// (below when there is no room) and never covers the pointer's target.
//
// It never stays behind: the element can move, be disabled or be replaced
// under a pointer that does not move (a click that opens a menu, a re-render),
// and then no leave arrives. So while shown it also hides on any press, on
// the pointer moving off the element, on scroll, on Escape anywhere and when
// the window loses focus; the delayed show only happens if the pointer is
// still over it, and not again after a press until the pointer leaves.
//
// Touch (`tap`): a finger has no hover, so by default a tooltip never shows on
// touch. With `tap`, a finger press shows it at once (the press is not
// cancelled, so the click still happens). The leave/move that follow lifting
// the finger do not hide it; it hides on the next press elsewhere, scroll,
// Escape, or the element losing focus. Long press shows no callout and selects
// no text on the element. Use it where touch has nothing else to tell the
// meaning (a disabled control's reason, a done mark).
//
// Hold (`hold`): only a finger resting on the element (HOLD_MS) shows it, and
// the press then does not count as a tap (a link is not followed); a pointer
// at rest or the focus never shows it. For icons without labels that a tap
// already uses (the iPhone Duo's column of icons), as the system's own bars.
import {
  cloneElement,
  type ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { QUIET_FOCUS_ATTR } from "./quiet-focus";

const DELAY_MS = 450;
/** How long a finger rests before a `hold` tooltip shows. */
const HOLD_MS = 500;

export function Tooltip({
  text,
  side = "above",
  tap = false,
  hold = false,
  children,
}: {
  text: string;
  /** Also show on a finger press (touch has no hover). */
  tap?: boolean;
  /** Show only on a finger's long press, which then is not a tap. */
  hold?: boolean;
  /**
   * "right": beside the element (icons in a narrow column). "below": under
   * it (a list's title at a phone's top, where above is the status bar).
   */
  side?: "above" | "below" | "right";
  children: ReactElement<Record<string, unknown>>;
}) {
  const [at, setAt] = useState<{
    x: number;
    y: number;
    transform: string;
  } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const anchor = useRef<HTMLElement | null>(null);
  // After a press, the element re-rendered under a resting pointer must not
  // bring the tooltip back; only leaving the element does.
  const pressed = useRef(false);
  // Shown by a finger press: the leave/move that follow lifting the finger
  // must not hide it.
  const tapped = useRef(false);
  // A long press showed it: the click that follows is swallowed.
  const held = useRef(false);
  const show = useCallback(
    (el: HTMLElement) => {
      if (!el.isConnected) return;
      anchor.current = el;
      const r = el.getBoundingClientRect();
      if (side === "right") {
        setAt({
          x: r.right + 8,
          y: r.top + r.height / 2,
          transform: "translate(0, -50%)",
        });
        return;
      }
      const below = side === "below" || r.top < 40;
      setAt({
        x: r.left + r.width / 2,
        y: below ? r.bottom + 6 : r.top - 6,
        transform: `translate(-50%, ${below ? "0" : "-100%"})`,
      });
    },
    [side],
  );
  const cancel = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }, []);
  const hide = useCallback(() => {
    cancel();
    tapped.current = false;
    setAt(null);
  }, [cancel]);

  useEffect(() => cancel, [cancel]);

  const shown = at !== null;
  useEffect(() => {
    if (!shown) return;
    const onMove = (e: PointerEvent) => {
      if (tapped.current && e.pointerType !== "mouse") return;
      const el = anchor.current;
      if (!el || !(e.target instanceof Node) || !el.contains(e.target)) hide();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") hide();
    };
    const opts = { capture: true, passive: true } as const;
    document.addEventListener("pointermove", onMove, opts);
    const onDown = (e: PointerEvent) => {
      // The press that opened it by tap is handled by the element itself.
      const el = anchor.current;
      if (
        tapped.current &&
        el &&
        e.target instanceof Node &&
        el.contains(e.target)
      )
        return;
      hide();
    };
    document.addEventListener("pointerdown", onDown, opts);
    document.addEventListener("scroll", hide, opts);
    document.addEventListener("keydown", onKey, opts);
    window.addEventListener("blur", hide);
    return () => {
      document.removeEventListener("pointermove", onMove, opts);
      document.removeEventListener("pointerdown", onDown, opts);
      document.removeEventListener("scroll", hide, opts);
      document.removeEventListener("keydown", onKey, opts);
      window.removeEventListener("blur", hide);
    };
  }, [shown, hide]);

  const props = children.props as Record<string, (e: unknown) => void>;
  const childClass = (children.props as { className?: unknown }).className;
  const chain =
    (name: string, own: (e: { currentTarget: HTMLElement }) => void) =>
    (e: { currentTarget: HTMLElement }) => {
      own(e);
      props[name]?.(e);
    };
  return (
    <>
      {cloneElement(children, {
        // In the desktop app's title bar strip, a drag region swallows the
        // pointer: the element is taken out of it (styles: `[data-title-bar]`).
        "data-tooltip": "",
        ...((tap || hold) && {
          className: `${typeof childClass === "string" ? `${childClass} ` : ""}select-none [-webkit-touch-callout:none]`,
        }),
        onPointerEnter: chain("onPointerEnter", (e) => {
          const el = e.currentTarget;
          cancel();
          if (hold) return;
          if (pressed.current) return;
          timer.current = setTimeout(() => {
            timer.current = null;
            if (el.matches(":hover")) show(el);
          }, DELAY_MS);
        }),
        onPointerLeave: chain("onPointerLeave", (e) => {
          // Lifting a finger sends a leave; a tap-shown tooltip stays.
          if (
            tapped.current &&
            (e as unknown as PointerEvent).pointerType !== "mouse"
          )
            return;
          pressed.current = false;
          hide();
        }),
        onPointerDown: chain("onPointerDown", (e) => {
          if (hold) {
            cancel();
            held.current = false;
            if ((e as unknown as PointerEvent).pointerType === "mouse") return;
            const el = e.currentTarget;
            timer.current = setTimeout(() => {
              timer.current = null;
              show(el);
              tapped.current = true;
              held.current = true;
            }, HOLD_MS);
            return;
          }
          if (tap && (e as unknown as PointerEvent).pointerType !== "mouse") {
            cancel();
            show(e.currentTarget);
            tapped.current = true;
            return;
          }
          pressed.current = true;
          hide();
        }),
        onClick: chain("onClick", (e) => {
          if (held.current) {
            held.current = false;
            (e as unknown as Event).preventDefault();
            return;
          }
          if (!tapped.current) hide();
        }),
        ...(hold && {
          // Lifted or taken over (a scroll) before the rest is long enough.
          onPointerUp: chain("onPointerUp", cancel),
          onPointerCancel: chain("onPointerCancel", cancel),
          // No callout menu on the long press.
          onContextMenu: chain("onContextMenu", (e) => {
            (e as unknown as Event).preventDefault();
          }),
        }),
        onFocus: chain("onFocus", (e) => {
          if (hold) return;
          // Focus handed back by a closing layer (quietFocus) is not a keyboard move.
          if (
            e.currentTarget.matches(":focus-visible") &&
            !e.currentTarget.hasAttribute(QUIET_FOCUS_ATTR)
          )
            show(e.currentTarget);
        }),
        onBlur: chain("onBlur", hide),
        onKeyDown: chain("onKeyDown", (e) => {
          if ((e as unknown as KeyboardEvent).key === "Escape") hide();
        }),
      })}
      {at && (
        <div
          role="tooltip"
          // Near a screen edge, slide the label back inside instead of letting it overflow.
          ref={(node) => {
            if (!node) return;
            node.style.marginLeft = "0px";
            const r = node.getBoundingClientRect();
            const room = document.documentElement.clientWidth - 8;
            const shift =
              r.right > room ? room - r.right : r.left < 8 ? 8 - r.left : 0;
            if (shift) node.style.marginLeft = `${shift}px`;
          }}
          className="pointer-events-none fixed z-[60] whitespace-nowrap rounded-[6px] bg-fg px-2 py-1 text-[11.5px] text-surface"
          style={{
            left: at.x,
            top: at.y,
            transform: at.transform,
          }}
        >
          {text}
        </div>
      )}
    </>
  );
}
